import React, {
  useImperativeHandle,
  useRef,
  useEffect,
  forwardRef,
  useState,
} from "react";
import { Modal } from "bootstrap";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import HttpAPICall from "../../services/HttpAPICall";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../components/ui/Loader/Loader";

const CountUpdate = forwardRef(({ 
  onIamCountUpdate,
  onInventoryCountUpdate,
  onHrmCountUpdate,
  onCrmDealCountUpdate
}, ref) => {
  const access_token = useSelector((state) => state.auth.access_token);
  const modalElem = useRef(null);
  const searchModal = useRef(null);
  const [formLoading, setFormLoading] = useState(false);
  const [moduleType, setModuleType] = useState('iam'); 

  const [formData, setFormData] = useState({
    total_assets: "",
    total_users: "",
    total_sites: "",
    total_warehouses: "",
    total_items: "",
    total_employees: "",
    total_deals: "",
    module_id: null,
    tap_master_id: null,
  });

  useImperativeHandle(ref, () => ({
    openEditModal: (moduleType, data) => {
      setModuleType(moduleType);
      
      const newFormData = {
        module_id: data.module_id || null,
        tap_master_id: data.tap_master_id || null,
      };

      switch(moduleType) {
        case 'iam':
          newFormData.total_assets = data.total_assets || "";
          newFormData.total_users = data.total_users || "";
          newFormData.total_sites = data.total_sites || "";
          break;
        case 'inventory':
          newFormData.total_warehouses = data.total_warehouses || "";
          newFormData.total_items = data.total_items || "";
          break;
        case 'hrm':
          newFormData.total_employees = data.total_employees || "";
          break;
        case 'crm_deal':
          newFormData.total_deals = data.total_deals || "";
          break;
        default:
          break;
      }

      setFormData(newFormData);
      searchModal.current.show();
    },
    closeModal: () => {
      searchModalClose();
    },
  }));

  useEffect(() => {
    modalElem.current = document.getElementById("editModalID");
    searchModal.current = new Modal(modalElem.current, {
      keyboard: false,
      backdrop: "static",
    });

    const handleHidden = () => {
      setFormLoading(false);
    };

    modalElem.current.addEventListener("hidden.bs.modal", handleHidden);

    return () => {
      if (searchModal.current) {
        searchModal.current.dispose();
      }
      if (modalElem.current) {
        modalElem.current.removeEventListener("hidden.bs.modal", handleHidden);
      }
    };
  }, []);

  const searchModalClose = () => {
    if (searchModal.current) {
      searchModal.current.hide();
    }
    setFormLoading(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value !== "" ? parseInt(value) : 0,
    }));
  };

  const getModalTitle = () => {
    switch(moduleType) {
      case 'iam': return 'Edit IAM Counts';
      case 'inventory': return 'Edit Inventory Counts';
      case 'hrm': return 'Edit HRM Counts';
      case 'crm_deal': return 'Edit CRM Deal Counts';
      default: return 'Edit Counts';
    }
  };

  const renderFormFields = () => {
    switch(moduleType) {
      case 'iam':
        return (
          <>
            <div className="row align-items-center py-2 justify-content-center">
              <label className="col-sm-3 form-label require">Total Assets</label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  name="total_assets"
                  value={formData.total_assets}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  placeholder="Enter total assets"
                />
              </div>
            </div>
            <div className="row align-items-center py-2 justify-content-center">
              <label className="col-sm-3 form-label require">Total Sites</label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  name="total_sites"
                  value={formData.total_sites}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  placeholder="Enter total sites"
                />
              </div>
            </div>
            <div className="row align-items-center py-2 justify-content-center">
              <label className="col-sm-3 form-label require">Total Users</label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  name="total_users"
                  value={formData.total_users}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  placeholder="Enter total users"
                />
              </div>
            </div>
          </>
        );

      case 'inventory':
        return (
          <>
            <div className="row align-items-center py-2 justify-content-center">
              <label className="col-sm-3 form-label require">Total Warehouses</label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  name="total_warehouses"
                  value={formData.total_warehouses}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  placeholder="Enter total warehouses"
                />
              </div>
            </div>
            <div className="row align-items-center py-2 justify-content-center">
              <label className="col-sm-3 form-label require">Total Items</label>
              <div className="col-sm-8">
                <input
                  type="number"
                  className="form-control"
                  name="total_items"
                  value={formData.total_items}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                  required
                  placeholder="Enter total items"
                />
              </div>
            </div>
          </>
        );

      case 'hrm':
        return (
          <div className="row align-items-center py-2 justify-content-center">
            <label className="col-sm-3 form-label require">Total Employees</label>
            <div className="col-sm-8">
              <input
                type="number"
                className="form-control"
                name="total_employees"
                value={formData.total_employees}
                onChange={handleInputChange}
                min="0"
                step="1"
                required
                placeholder="Enter total employees"
              />
            </div>
          </div>
        );

      case 'crm_deal':
        return (
          <div className="row align-items-center py-2 justify-content-center">
            <label className="col-sm-3 form-label require">Total Deals</label>
            <div className="col-sm-8">
              <input
                type="number"
                className="form-control"
                name="total_deals"
                value={formData.total_deals}
                onChange={handleInputChange}
                min="0"
                step="1"
                required
                placeholder="Enter total deals"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getSubmitPayload = () => {
    const basePayload = {
      tap_master_id: formData.tap_master_id,
      module_id: formData.module_id,
    };

    switch(moduleType) {
      case 'iam':
        return {
          ...basePayload,
          total_assets: formData.total_assets,
          total_users: formData.total_users,
          total_sites: formData.total_sites,
        };
      case 'inventory':
        return {
          ...basePayload,
          total_warehouses: formData.total_warehouses,
          total_items: formData.total_items,
        };
      case 'hrm':
        return {
          ...basePayload,
          total_employees: formData.total_employees,
        };
      case 'crm_deal':
        return {
          ...basePayload,
          total_deals: formData.total_deals,
        };
      default:
        return basePayload;
    }
  };

  const getEndpoint = () => {
    switch(moduleType) {
      case 'iam':
        return '/group/update_iam_count';
      case 'inventory':
        return '/group/update_isc_count';
      case 'hrm':
        return '/group/update_hrm_count';
      case 'crm_deal':
        return '/group/update_scrm_count';
      default:
        return '/group/update_count';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    const fields = Object.keys(formData).filter(key => 
      !['module_id', 'tap_master_id'].includes(key)
    );
    
    for (let field of fields) {
      if (formData[field] < 0) {
        toast.error("Values cannot be negative", {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }
    }

    setFormLoading(true);
    const payload = getSubmitPayload();
    const endpoint = getEndpoint();

    console.log('Submitting payload:', payload);
    console.log('Endpoint:', endpoint);

    HttpAPICall.withAthorization(
      "PUT",
      BACKOFFICE_BASE_URL + endpoint,
      access_token,
      {},
      payload,
      (resp) => {
        toast.success("Counts updated successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });

        switch(moduleType) {
          case 'iam':
            if (onIamCountUpdate) {
              onIamCountUpdate({
                total_assets: payload.total_assets,
                total_users: payload.total_users,
                total_sites: payload.total_sites
              });
            }
            break;
          case 'inventory':
            if (onInventoryCountUpdate) {
              onInventoryCountUpdate({
                total_warehouses: payload.total_warehouses,
                total_items: payload.total_items
              });
            }
            break;
          case 'hrm':
            if (onHrmCountUpdate) {
              onHrmCountUpdate({
                total_employees: payload.total_employees
              });
            }
            break;
          case 'crm_deal':
            if (onCrmDealCountUpdate) {
              onCrmDealCountUpdate({
                total_deals: payload.total_deals
              });
            }
            break;
          default:
            break;
        }

        searchModalClose();
      },
      (error) => {
        toast.error(error?.message || "Failed to update counts", {
          position: toast.POSITION.TOP_RIGHT,
        });
        console.error("Count update error:", error);
      }
    ).finally(() => setFormLoading(false));
  };

  return (
    <>
      <div
        className="modal fade"
        id="editModalID"
        tabIndex="-1"
        ref={modalElem}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{getModalTitle()}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={searchModalClose}
                aria-label="Close"
                disabled={formLoading}
              />
            </div>

            <div className="modal-body">
              {formLoading ? (
                <div className="text-center py-5">
                  <Loader />
                </div>
              ) : (
                <form id="countEditForm" onSubmit={handleSubmit}>
                  <div className="row">
                    {renderFormFields()}
                  </div>
                </form>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={searchModalClose}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="countEditForm"
                className="btn btn-primary"
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Updating...
                  </>
                ) : (
                  "Update Counts"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default CountUpdate;